import { Component, OnInit } from '@angular/core';
import { Tax } from './tax';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { TaxService } from 'src/app/services/tax.service';
import { AssessmentService } from 'src/app/services/assessment.service';
import { Assessment } from 'src/app/models/assessment';
import { ToastrService } from 'ngx-toastr';
import { UnionDomain } from 'src/app/services/union.domain';
import { FormControl, FormGroup } from '@angular/forms';
import { SmsService } from 'src/app/services/sms.service';
import { UnionService } from 'src/app/services/union.service';
import { Union } from 'src/app/models/union';

@Component({
  selector: 'app-tax-collection',
  templateUrl: './tax-collection.component.html',
  styleUrls: ['./tax-collection.component.scss'],
})
export class TaxCollectionComponent implements OnInit {
  taxList: Tax[] = [];
  qrCode!: string;
  $subscriptions: Subscription[] = [];
  assessment: Assessment = new Assessment();
  yearList: string[] = [];
  startYear: string = '2025';
  defaultFinancialYear: string = '2025';
  taxForm!: FormGroup;
  unionData!: Union;
  isLoading: boolean = false;
  outstandingAmount: number = 0;
  Math = Math; // Make Math available in template

  constructor(
    private route: ActivatedRoute,
    private taxService: TaxService,
    private assessmentService: AssessmentService,
    private toastr: ToastrService,
    public unionDomain: UnionDomain,
    private smsService: SmsService,
    private unionService: UnionService
  ) {}

  async ngOnInit(): Promise<void> {
    this.qrCode = this.route.snapshot.queryParams['qr_code'];
    await this.getUnion();
    this.generateYearList();
    this.getTaxList();
    this.getAssesment();
    this.initializeForm();
  }

  getUnion = async (): Promise<void> => {
    this.$subscriptions.push(
      this.unionService.getSingleUnion().subscribe({
        next: (res) => {
          this.unionData = res;
          // Set default financial year from union data
          if (this.unionData.current_financial_year) {
            this.defaultFinancialYear = this.unionData.current_financial_year.toString();
          }
          console.log('Union data loaded, default financial year:', this.defaultFinancialYear);
        },
        error: (err) => {
          this.toastr.error(err.message);
        },
      })
    );
  };

  private initializeForm = (formData?: Tax) => {
    this.taxForm = new FormGroup({
      financial_year: new FormControl(
        formData?.financial_year ? formData.financial_year : this.defaultFinancialYear
      ),
      amount: new FormControl(formData?.amount ? formData.amount : 0),
      debit_credit: new FormControl(
        formData?.debit_credit ? formData.debit_credit : 'D'
      ),
      assessment_qr_code: new FormControl(this.qrCode),
    });
  };

  onSubmit() {
    this.taxService
      .debitOrCreditSingleAssessment(this.taxForm.value)
      .subscribe({
              next: (res) => {
        this.toastr.success(res.message);
        if (this.taxForm.value.debit_credit == 'D') {
          this.sendSms();
        }
        this.reloadTaxList();
      },
        error: (err) => {
          this.toastr.error(err.error.message);
        },
      });
  }
  sendSms() {
    if (this.unionData.is_tax_collection_msg_on) {
      this.$subscriptions.push(
        this.smsService
          .sendTaxCollectionSms(
            this.assessment,
            this.unionData,
            this.taxForm.value.amount
          )
          .subscribe({
            next: (res) => {
              if (res.response_code == 202) {
                this.toastr.success(res.success_message);
              } else {
                this.toastr.error(res.error_message);
                alert('অপর্যাপ্ত ব্যালেন্স | SMS সম্পন্ন হয়নি');
              }
              console.log(res);
            },
          })
      );
    }
  }

  getAssesment = (): void => {
    this.$subscriptions.push(
      this.assessmentService.getSingleAssessmentByUUID(this.qrCode).subscribe({
        next: (res) => {
          this.assessment = res;
          console.log(this.assessment);
        },
        error: (err) => {
          this.toastr.error(err.message);
        },
      })
    );
  };

  private async reloadTaxList() {
    if (!this.qrCode) {
      this.toastr.error('QR Code not found');
      return;
    }
    
    (await this.taxService.getTaxList(this.qrCode)).subscribe({
      next: (res) => {
        this.taxList = res || [];
        console.log('Tax list reloaded:', this.taxList);
        
        // Recalculate outstanding amount after reload
        this.calculateOutstandingAmount();
        this.updateTaxAmountField();
      },
      error: (err) => {
        console.error('Error reloading tax list:', err);
        this.toastr.error(err.error?.message || err.message || 'Error reloading tax data');
        this.taxList = [];
      },
    });
  }

  private async getTaxList() {
    if (!this.qrCode) {
      this.toastr.error('QR Code not found in URL');
      return;
    }
    
    this.isLoading = true;
    (await this.taxService.getTaxList(this.qrCode)).subscribe({
      next: (res) => {
        this.taxList = res || [];
        this.isLoading = false;
        console.log('Tax list loaded:', this.taxList);
        
        // Calculate outstanding amount and update form
        this.calculateOutstandingAmount();
        this.updateTaxAmountField();
        
        if (this.taxList.length === 0) {
          console.log('No tax records found for this assessment');
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error loading tax list:', err);
        this.toastr.error(err.error?.message || err.message || 'Error loading tax data');
        this.taxList = [];
      },
    });
  }

  async deleteTax(taxId: number) {
    if (confirm('Are you sure you want to delete ?'))
      (await this.taxService.deleteTaxCollection(taxId)).subscribe({
        next: (res) => {
          this.reloadTaxList();
        },
      });
  }

  Number(year: string) {
    return Number(year);
  }

  trackByTaxId(index: number, tax: Tax): number {
    return tax.id;
  }

  // Calculate outstanding amount across all years
  calculateOutstandingAmount(): void {
    let totalCredit = 0;
    let totalDebit = 0;
    
    // Process all tax records regardless of year
    this.taxList.forEach(tax => {
      const amount = Number(tax.amount) || 0;
      if (tax.debit_credit === 'C') {
        // Credit - Tax assigned/charged
        totalCredit += amount;
      } else if (tax.debit_credit === 'D') {
        // Debit - Tax paid
        totalDebit += amount;
      }
    });
    
    // Outstanding = total assigned - total paid (across all years)
    this.outstandingAmount = totalCredit - totalDebit;
    
    console.log('Outstanding calculation (all years):', {
      totalCredit,
      totalDebit,
      outstandingAmount: this.outstandingAmount,
      totalRecords: this.taxList.length
    });
  }

  // Update the tax amount field with outstanding amount
  updateTaxAmountField(): void {
    if (this.taxForm && this.outstandingAmount > 0) {
      this.taxForm.get('amount')?.setValue(this.outstandingAmount);
      console.log('Updated tax amount field to:', this.outstandingAmount);
    }
  }

  // Handle financial year change to recalculate outstanding amount
  onFinancialYearChange(): void {
    this.calculateOutstandingAmount();
    this.updateTaxAmountField();
  }

  // Generate year list from 2025 to 10 years in the future
  private generateYearList(): void {
    this.yearList = [];
    const startYear = 2025;
    const endYear = startYear + 10; // 10 years into the future
    
    for (let year = startYear; year <= endYear; year++) {
      this.yearList.push(year.toString());
    }
    
    console.log('Generated year list:', this.yearList);
  }

  ngOnDestroy(): void {
    this.$subscriptions.forEach((s) => s.unsubscribe());
  }
}
